/**
 * Product gallery with full-screen zoom (snippets/product-gallery.liquid).
 * Auftrag "Galerie ohne Rahmen, größer, Zoom schwebend, Blättern" (02.10.2026). No library.
 *
 * - Fit (assets/photo-fit.js): for every photo a 160-px copy is measured. If its border is
 *   transparent (= cut-out photo), the photo is scaled so that its visible area nearly fills the
 *   stage, standing on a common floor line. Photos without transparency stay "contain" (with the editor setting
 *   "Produktfoto vergrößern" as before).
 * - Normal view: native scroll snap (swipe), arrow buttons, arrow keys while the gallery has focus,
 *   counter, existing thumbnails, tabs Photo/Section/Dimensions/In use.
 * - Zoom: modal <dialog> with focus trap, Esc and the close symbol, focus returns to the photo button.
 *   Wheel / click / pinch zoom further, drag pans, swipe or arrows change the photo. The position is
 *   shared between the two views.
 * - Variant change: variant-picker.js dispatches "gallery:select" ({ mediaId, variantId }).
 *   Variantenbilder (V3, doku/regeln/VARIANTEN-BILDER-LOGIK.md): the gallery is filtered to the
 *   variant's images (its group, then the general images); groups come as JSON from
 *   snippets/variant-media-group.liquid ([data-pg-variants]). No variant image: all media.
 * - prefers-reduced-motion: no smooth scrolling, no zoom animation.
 */
(function () {
  if (window.CryonosGallery) return;
  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var ZOOM_MAX = 5;
  var CLICK_ZOOM = 2.5;

  /* Measuring and scaling come from assets/photo-fit.js (shared helper, loaded before this file). */
  var PF = window.CryonosPhotoFit;
  if (!PF) return;
  var probe = PF.probe, fitValues = PF.fitValues, applyFit = PF.apply;

  function focusables(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])'),
      function (el) { return !el.disabled && el.offsetParent !== null; }
    );
  }

  function Gallery(root) {
    this.root = root;
    this.list = root.querySelector('[data-pg-list]');
    this.items = Array.prototype.slice.call(root.querySelectorAll('.pg__item'));
    this.thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-pg-thumb]'));
    this.thumbsWrap = root.querySelector('.pg__thumbs');
    this.tPrev = root.querySelector('[data-pg-tprev]');
    this.tNext = root.querySelector('[data-pg-tnext]');
    this.allItems = this.items.slice();
    var vmEl = root.querySelector('script[data-pg-variants]');
    try { this.vm = vmEl ? JSON.parse(vmEl.textContent) : null; } catch (err) { this.vm = null; }
    this.indexEl = root.querySelector('[data-pg-index]');
    this.totalEl = root.querySelector('[data-pg-total]');
    this.dialog = root.querySelector('[data-pg-dialog]');
    this.index = 0;
    var stage = root.closest('.buy-pd__stage');
    var zoomVar = stage ? parseFloat(getComputedStyle(stage).getPropertyValue('--pd-zoom')) : 1;
    this.baseScale = isNaN(zoomVar) || zoomVar <= 0 ? 1 : zoomVar;
    this.bind();
    this.fitAll();
    this.update();
  }

  Gallery.prototype.visible = function () {
    return this.items.filter(function (li) { return getComputedStyle(li).display !== 'none'; });
  };

  Gallery.prototype.bind = function () {
    var self = this;
    var prev = this.root.querySelector('[data-pg-prev]');
    var next = this.root.querySelector('[data-pg-next]');
    if (prev) prev.addEventListener('click', function () { self.go(self.index - 1); });
    if (next) next.addEventListener('click', function () { self.go(self.index + 1); });

    // Sync the index only once the scroll has settled: during a smooth scroll the intermediate
    // positions would mark the wrong photo as current (and make the focused button inert).
    // Programmatic scrolls (arrows, keys, thumbnails, variant) never change the index on the way:
    // the target is already set in go(); at the end the position is only corrected to the target.
    var settle = null;
    this.list.addEventListener('scroll', function () {
      clearTimeout(settle);
      settle = setTimeout(function () {
        if (self.programmatic) {
          self.programmatic = false;
          var cur = self.visible()[self.index];
          if (cur && Math.abs(self.list.scrollLeft - cur.offsetLeft) > 2) self.list.scrollTo({ left: cur.offsetLeft, behavior: 'instant' });
          return;
        }
        self.syncFromScroll();
      }, 140);
    }, { passive: true });
    // A user swipe/drag takes over again from programmatic scrolling.
    ['pointerdown', 'touchstart', 'wheel'].forEach(function (t) {
      self.list.addEventListener(t, function () { self.programmatic = false; }, { passive: true });
    });

    this.root.addEventListener('keydown', function (e) {
      if (self.dialog && self.dialog.contains(e.target)) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        if (e.target.closest('.product-gallery__tabs')) return;
        e.preventDefault();
        self.go(self.index + (e.key === 'ArrowRight' ? 1 : -1));
        var cur = self.visible()[self.index];
        var btn = cur && cur.querySelector('[data-pg-open]');
        if (btn && e.target.closest('.pg__item')) btn.focus({ preventScroll: true });
        // AUFTRAG-92: on a thumbnail the focus follows the active thumbnail (roving like a tab row).
        if (e.target.closest('[data-pg-thumb]') && cur) {
          var th = self.thumbs.filter(function (a) { return a.getAttribute('data-pg-thumb') === cur.getAttribute('data-media-id'); })[0];
          if (th) th.focus({ preventScroll: true });
        }
      }
    });

    // Thumbnail strip: arrows page through it (mouse), it scrolls by touch/wheel/keys too.
    if (this.thumbsWrap) {
      var tw = this.thumbsWrap;
      var page = function (dir) { tw.scrollBy({ left: dir * Math.max(60, tw.clientWidth * 0.8), behavior: reduceMotion.matches ? 'instant' : 'smooth' }); };
      if (this.tPrev) this.tPrev.addEventListener('click', function () { page(-1); });
      if (this.tNext) this.tNext.addEventListener('click', function () { page(1); });
      tw.addEventListener('scroll', function () { self.updateThumbArrows(); }, { passive: true });
      window.addEventListener('resize', function () { self.updateThumbArrows(); });
      if ('ResizeObserver' in window) new ResizeObserver(function () { self.updateThumbArrows(); }).observe(tw);
    }

    this.thumbs.forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        self.goToMedia(a.getAttribute('data-pg-thumb'));
      });
    });

    this.items.forEach(function (li) {
      var btn = li.querySelector('[data-pg-open]');
      if (btn) btn.addEventListener('click', function () {
        var i = self.visible().indexOf(li);
        if (i > -1) self.openZoom(i);
      });
    });

    var tabs = Array.prototype.slice.call(this.root.querySelectorAll('.product-gallery__tab'));
    tabs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.setGroup(btn.getAttribute('data-gallery-tab'));
      });
    });

    document.addEventListener('gallery:select', function (e) {
      if (!e.detail) return;
      if (e.detail.variantId && self.vm) self.setVariant(String(e.detail.variantId));
      else if (e.detail.mediaId) self.goToMedia(String(e.detail.mediaId));
    });

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () { self.fitAll(); self.go(self.index, false); });
      ro.observe(this.list);
    } else {
      window.addEventListener('resize', function () { self.fitAll(); });
    }

    if (this.dialog) this.bindZoom();
  };

  Gallery.prototype.setGroup = function (group) {
    this.root.setAttribute('data-active-group', group);
    Array.prototype.forEach.call(this.root.querySelectorAll('.product-gallery__tab'), function (b) {
      var on = b.getAttribute('data-gallery-tab') === group;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    this.index = 0;
    this.list.scrollLeft = 0;
    this.fitAll();
    this.update();
  };

  // Variantenbilder: re-order and filter the photos (and thumbnails) to the variant's display list.
  Gallery.prototype.setVariant = function (variantId) {
    var vm = this.vm || {};
    var start = vm.v ? vm.v[variantId] : null;
    var ids = start && vm.g && vm.g[start] ? vm.g[start].map(String) : null;
    var byId = function (arr, attr) {
      var m = {};
      arr.forEach(function (el) { m[el.getAttribute(attr)] = el; });
      return m;
    };
    var liMap = byId(this.allItems, 'data-media-id');
    var order = [];
    if (ids) ids.forEach(function (id) { if (liMap[id] && order.indexOf(liMap[id]) < 0) order.push(liMap[id]); });
    var rest = this.allItems.filter(function (li) { return order.indexOf(li) < 0; });
    var list = this.list;
    var all = order.concat(rest);
    all.forEach(function (li) {
      li.classList.toggle('is-vhide', !!ids && order.indexOf(li) < 0);
      list.appendChild(li);
    });
    this.items = all;
    if (this.thumbsWrap) {
      var thMap = byId(this.thumbs, 'data-pg-thumb');
      var wrap = this.thumbsWrap;
      all.forEach(function (li) {
        var a = thMap[li.getAttribute('data-media-id')];
        if (!a) return;
        a.classList.toggle('is-vhide', li.classList.contains('is-vhide'));
        wrap.appendChild(a);
      });
    }
    this.root.classList.toggle('pg--single', (ids ? order.length : all.length) < 2);
    if (this.root.getAttribute('data-active-group') && this.root.getAttribute('data-active-group') !== 'photo') {
      this.setGroup('photo');
      return;
    }
    this.index = 0;
    this.programmatic = false;
    list.scrollLeft = 0;
    this.fitAll();
    this.update();
  };

  Gallery.prototype.fitAll = function () {
    var self = this;
    this.items.forEach(function (li) {
      var fit = li.querySelector('[data-pg-fit]');
      if (!fit) return;
      probe(fit.getAttribute('data-probe')).then(function (bb) {
        var W = li.clientWidth, H = li.clientHeight;
        var v = fitValues(W, H, bb, { w: 0.9, h: 0.92, base: 0.965, max: 3 });
        li.classList.toggle('is-cut', !!v);
        applyFit(fit, v, self.baseScale);
      });
    });
  };

  Gallery.prototype.syncFromScroll = function () {
    var w = this.list.clientWidth || 1;
    var i = Math.round(this.list.scrollLeft / w);
    if (i !== this.index) { this.index = i; this.update(); }
  };

  Gallery.prototype.go = function (i, smooth) {
    var v = this.visible();
    var n = v.length;
    if (!n) return;
    i = ((i % n) + n) % n;
    this.index = i;
    var behavior = smooth === false || reduceMotion.matches ? 'instant' : 'smooth';
    if (Math.abs(this.list.scrollLeft - v[i].offsetLeft) > 2) this.programmatic = true;
    this.update();
    this.list.scrollTo({ left: v[i].offsetLeft, behavior: behavior });
  };

  Gallery.prototype.goToMedia = function (mediaId) {
    var li = this.items.filter(function (x) { return x.getAttribute('data-media-id') === mediaId; })[0];
    if (!li || li.classList.contains('is-vhide')) return;
    if (getComputedStyle(li).display === 'none') this.setGroup(li.getAttribute('data-group') || 'photo');
    var i = this.visible().indexOf(li);
    if (i > -1) this.go(i);
  };

  // Show the strip arrows only while thumbnails are cut off on that side.
  Gallery.prototype.updateThumbArrows = function () {
    var tw = this.thumbsWrap;
    if (!tw) return;
    var max = tw.scrollWidth - tw.clientWidth;
    var moreL = max > 2 && tw.scrollLeft > 2, moreR = max > 2 && tw.scrollLeft < max - 2;
    // AUFTRAG-92: with overflow both arrows keep their place (stable strip width, nothing shifts under the
    // active thumbnail); the one at the end of the strip is only made invisible (.is-off).
    var over = max > 2;
    if (this.tPrev) { this.tPrev.hidden = !over; this.tPrev.classList.toggle('is-off', !moreL); }
    if (this.tNext) { this.tNext.hidden = !over; this.tNext.classList.toggle('is-off', !moreR); }
    // AUFTRAG-92: soft fade on the side where more thumbnails follow.
    tw.classList.toggle('is-more-l', moreL);
    tw.classList.toggle('is-more-r', moreR);
  };

  // Keep the active thumbnail inside the strip (scrolls the strip only, never the page).
  Gallery.prototype.revealThumb = function (a) {
    var tw = this.thumbsWrap;
    if (!tw || !a || a.offsetParent === null) return;
    var left = a.getBoundingClientRect().left - tw.getBoundingClientRect().left + tw.scrollLeft;
    var right = left + a.offsetWidth;
    var b = reduceMotion.matches ? 'instant' : 'smooth';
    var pad = 32; // clear of the 28-px edge fade
    if (left < tw.scrollLeft + pad) tw.scrollTo({ left: Math.max(0, left - pad), behavior: b });
    else if (right > tw.scrollLeft + tw.clientWidth - pad) tw.scrollTo({ left: right - tw.clientWidth + pad, behavior: b });
  };

  Gallery.prototype.update = function () {
    var v = this.visible();
    var cur = v[this.index];
    // Never make the focused element inert: move the focus to the current photo first.
    var active = document.activeElement;
    var activeLi = active && active.closest ? active.closest('.pg__item') : null;
    if (activeLi && cur && activeLi !== cur && this.list.contains(activeLi)) {
      cur.removeAttribute('inert');
      var cb = cur.querySelector('[data-pg-open]');
      if (cb) cb.focus({ preventScroll: true });
    }
    this.items.forEach(function (li) {
      var on = li === cur;
      li.classList.toggle('is-current', on);
      if (on) li.removeAttribute('inert'); else li.setAttribute('inert', '');
    });
    if (this.indexEl) this.indexEl.textContent = String(this.index + 1);
    if (this.totalEl) this.totalEl.textContent = String(v.length);
    var id = cur ? cur.getAttribute('data-media-id') : '';
    var self = this;
    this.thumbs.forEach(function (a) {
      var on = a.getAttribute('data-pg-thumb') === id;
      a.classList.toggle('is-active', on);
      if (on) { a.setAttribute('aria-current', 'true'); self.revealThumb(a); } else a.removeAttribute('aria-current');
    });
    this.updateThumbArrows();
  };

  /* ---------- Zoom dialog ---------- */
  Gallery.prototype.bindZoom = function () {
    var self = this;
    var d = this.dialog;
    this.zStage = d.querySelector('[data-pgz-stage]');
    this.zPan = d.querySelector('[data-pgz-pan]');
    this.zIndexEl = d.querySelector('[data-pgz-index]');
    this.z = { s: 1, x: 0, y: 0 };

    d.querySelector('[data-pgz-close]').addEventListener('click', function () { d.close(); });
    var zp = d.querySelector('[data-pgz-prev]');
    var zn = d.querySelector('[data-pgz-next]');
    if (zp) zp.addEventListener('click', function () { self.zoomStep(-1); });
    if (zn) zn.addEventListener('click', function () { self.zoomStep(1); });

    d.addEventListener('close', function () {
      document.documentElement.classList.remove('pgz-lock');
      self.go(self.zi, false);
      // The browser restores focus to the opener while closing; that photo may no longer be the
      // current (inert) one, so focus the current photo button once the dialog has settled.
      setTimeout(function () {
        var cur = self.visible()[self.index];
        var btn = cur && cur.querySelector('[data-pg-open]');
        if (btn) btn.focus({ preventScroll: true });
        else if (self.opener && self.opener.focus) self.opener.focus({ preventScroll: true });
      }, 0);
    });

    d.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        self.zoomStep(e.key === 'ArrowRight' ? 1 : -1);
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        self.zoomAt(self.zStage.clientWidth / 2, self.zStage.clientHeight / 2, self.z.s * 1.5, true);
      } else if (e.key === '-') {
        e.preventDefault();
        self.zoomAt(self.zStage.clientWidth / 2, self.zStage.clientHeight / 2, self.z.s / 1.5, true);
      } else if (e.key === 'Tab') {
        var f = focusables(d);
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    /* Pointer: pan, pinch, click zoom, swipe */
    var pts = {};
    var start = null, pinch = null, moved = false, multi = false;
    function count() { return Object.keys(pts).length; }
    function rel(e) { var r = self.zStage.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; }
    this.zStage.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      try { self.zStage.setPointerCapture(e.pointerId); } catch (err) { /* pointer already released */ }
      pts[e.pointerId] = rel(e);
      if (count() === 1) {
        multi = false; moved = false;
        start = { p: pts[e.pointerId], x: self.z.x, y: self.z.y };
      } else if (count() === 2) {
        multi = true;
        var k = Object.keys(pts), a = pts[k[0]], b = pts[k[1]];
        pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, s: self.z.s, x: self.z.x, y: self.z.y, m: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
      }
    });
    this.zStage.addEventListener('pointermove', function (e) {
      if (!(e.pointerId in pts)) return;
      pts[e.pointerId] = rel(e);
      if (count() === 2 && pinch) {
        var k = Object.keys(pts), a = pts[k[0]], b = pts[k[1]];
        var ns = Math.max(1, Math.min(ZOOM_MAX, pinch.s * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.d)));
        self.z.x = pinch.m.x - (pinch.m.x - pinch.x) * (ns / pinch.s);
        self.z.y = pinch.m.y - (pinch.m.y - pinch.y) * (ns / pinch.s);
        self.z.s = ns;
        self.applyZoom(false);
        moved = true;
      } else if (count() === 1 && start) {
        var p = pts[e.pointerId];
        var dx = p.x - start.p.x, dy = p.y - start.p.y;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) moved = true;
        if (self.z.s > 1 && moved) {
          self.zStage.classList.add('is-dragging');
          self.z.x = start.x + dx;
          self.z.y = start.y + dy;
          self.applyZoom(false);
        }
      }
    });
    function end(e) {
      if (!(e.pointerId in pts)) return;
      var p = pts[e.pointerId];
      delete pts[e.pointerId];
      self.zStage.classList.remove('is-dragging');
      if (count() > 0) { pinch = null; return; }
      if (!start) return;
      var dx = p.x - start.p.x, dy = p.y - start.p.y;
      if (!multi && e.type === 'pointerup') {
        if (!moved) {
          if (self.z.s > 1.01) self.zoomAt(p.x, p.y, 1, true);
          else self.zoomAt(p.x, p.y, CLICK_ZOOM, true);
        } else if (self.z.s <= 1.01 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          self.zoomStep(dx < 0 ? 1 : -1);
        }
      }
      start = null; pinch = null;
    }
    this.zStage.addEventListener('pointerup', end);
    this.zStage.addEventListener('pointercancel', end);
    this.zStage.addEventListener('wheel', function (e) {
      e.preventDefault();
      var p = rel(e);
      self.zoomAt(p.x, p.y, self.z.s * Math.exp(-e.deltaY * 0.0015), false);
    }, { passive: false });

    window.addEventListener('resize', function () {
      if (d.open) { self.fitZoom(); self.applyZoom(false); }
    });
  };

  Gallery.prototype.openZoom = function (i) {
    if (!this.dialog) return;
    this.opener = document.activeElement;
    this.zi = i;
    document.documentElement.classList.add('pgz-lock');
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    this.renderZoom();
    var close = this.dialog.querySelector('[data-pgz-close]');
    if (close) close.focus({ preventScroll: true });
  };

  Gallery.prototype.zoomStep = function (dir) {
    var n = this.visible().length;
    if (n < 2) return;
    this.zi = (((this.zi + dir) % n) + n) % n;
    this.renderZoom();
  };

  Gallery.prototype.renderZoom = function () {
    var li = this.visible()[this.zi];
    if (!li) return;
    var tpl = li.querySelector('template[data-pg-zoom-src]');
    this.zPan.innerHTML = '';
    if (tpl) this.zPan.appendChild(tpl.content.cloneNode(true));
    this.zImg = this.zPan.querySelector('img');
    if (this.zImg) this.zImg.setAttribute('draggable', 'false');
    var fit = li.querySelector('[data-pg-fit]');
    this.zProbe = fit ? fit.getAttribute('data-probe') : '';
    this.z = { s: 1, x: 0, y: 0 };
    this.applyZoom(false);
    this.fitZoom();
    if (this.zIndexEl) this.zIndexEl.textContent = String(this.zi + 1);
    var zt = this.dialog.querySelector('[data-pgz-total]');
    if (zt) zt.textContent = String(this.visible().length);
  };

  Gallery.prototype.fitZoom = function () {
    var self = this;
    var img = this.zImg;
    if (!img) return;
    probe(this.zProbe).then(function (bb) {
      if (img !== self.zImg) return;
      /* 08.10.2026 (Bild-Zoom): Das Foto passt immer vollständig in die Bühne (object-fit: contain, Rand 72/88/40 px).
         Die frühere Vergrößerung auf die sichtbare Fläche (max. Faktor 8) ließ Fotos über den Bildschirm hinausragen und
         war nicht verschiebbar. Details zeigt der Klick-Zoom (bis ZOOM_MAX) mit Verschieben. Nur der Bodenschatten bleibt. */
      var v = fitValues(self.zStage.clientWidth, self.zStage.clientHeight, bb, { w: 0.96, h: 0.96, base: 0.98, max: 8 });
      self.zPan.classList.toggle('is-cut', !!v);
      applyFit(img, null, 1);
      if (v) self.zPan.style.setProperty('--sw', v.sw.toFixed(1) + '%');
    });
  };

  Gallery.prototype.zoomAt = function (mx, my, ns, animate) {
    ns = Math.max(1, Math.min(ZOOM_MAX, ns));
    this.z.x = mx - (mx - this.z.x) * (ns / this.z.s);
    this.z.y = my - (my - this.z.y) * (ns / this.z.s);
    this.z.s = ns;
    this.applyZoom(animate);
  };

  Gallery.prototype.applyZoom = function (animate) {
    var W = this.zStage.clientWidth, H = this.zStage.clientHeight;
    var z = this.z;
    if (z.s <= 1.001) { z.s = 1; z.x = 0; z.y = 0; }
    z.x = Math.min(0, Math.max(W - W * z.s, z.x));
    z.y = Math.min(0, Math.max(H - H * z.s, z.y));
    this.zPan.classList.toggle('is-anim', !!animate && !reduceMotion.matches);
    this.zPan.style.setProperty('--px', z.x.toFixed(1) + 'px');
    this.zPan.style.setProperty('--py', z.y.toFixed(1) + 'px');
    this.zPan.style.setProperty('--z', z.s.toFixed(4));
    this.zStage.classList.toggle('is-zoomed', z.s > 1);
  };

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pg]'), function (root) {
      if (!root.__pg) root.__pg = new Gallery(root);
    });
  }
  window.CryonosGallery = { init: init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
